<h4>What this screen is for</h4>
<p>
    Finding a train worth attention, then accepting or changing the bucket
    allocations FareVantage has recommended for it. Work flows top to bottom: pick a
    cell on the heatmap, then work in the grid underneath.
</p>
<p>
    <strong>The grid starts empty.</strong> Applying a filter loads the heatmap only.
    Right-click a heatmap cell and choose <strong>View Details</strong> (or
    <strong>View ODs</strong> on a tube-map segment) to populate the grid, the
    capacity chart and the shoulder chart.
</p>

<h4>Heatmap</h4>
<p>
    One tab per market, services across the top and dates down the side. The two
    dropdowns choose the sort order and which measure is shaded &mdash; capacity
    constraints, pacing, revenue opportunity or walk-up override. Only part of the
    grid is drawn at a time; drag the scrollbars, or hold shift and scroll, to move
    around. Resizing the window redraws it and loses your position.
</p>
<p>
    Hovering a cell gives the value behind the shading. The <strong>Colour-safe view</strong>
    switch in the card title is for anyone who cannot separate the green-to-red shading: it
    swaps the scale for blue through cream to red, prints a band number in every cell, and
    puts the same numbers in the key. Numbers run from 1 at the best end of the measure to 5
    at the worst, 6 on pacing, and W where a walk-up override is set. The switch also changes
    the BR dot, the Sensitivity tint in the grid below and the series colours in the Capacity
    and Shoulder charts. It stays on until you log out. Hovering a tube segment gives its
    value as well.
</p>
<p>
    Right-clicking a cell also offers <strong>Override Advance</strong>,
    <strong>Override Walk-Up</strong> and <strong>Toggle Auto Pilot</strong>.
</p>

<h4>Capacity and Shoulder Chart</h4>
<p>
    <strong>Capacity</strong> is leg-level: capacity, allocation, excess, flex
    forecast and walk-up demand for each leg of the train.
    <strong>Shoulder Chart</strong> compares booked and forecast demand on the
    chosen leg, with lead buckets in the tooltip.
</p>

<h4>Reading the grid</h4>
<p>Every row is one journey (origin to destination) on one service, date and class.</p>
<ul>
    <li><strong>OTB</strong> is the intraday bookings figure, and <strong>OTB &Delta;</strong> is how much it has moved since the overnight run.</li>
    <li><strong>Flx OTB</strong> and <strong>Flx Fcst</strong> report flex separately from the main columns.</li>
    <li><strong>Available</strong> is allocation less bookings; <strong>Excess</strong> also nets off remaining forecast.</li>
    <li>
        <strong>Empty Seats</strong> is what the forecast expects to go unsold: on the
        journey's tightest leg, capacity less bookings, less remaining forecast demand,
        less any walk-up demand that has been removed. It is a <em>leg</em> figure shown
        against a journey row, so it counts everyone travelling over that leg rather than
        only this journey's passengers, and it is never negative &mdash; a leg forecast to
        fill reads 0 rather than showing how far over it is.
    </li>
    <li><strong>FV</strong> shows the recommended profile, or your override once you have made one. <strong>Prev Profile</strong> is what was in force before.</li>
    <li>
        The <strong>Sensitivity</strong> cell is tinted green, yellow or red for low, medium
        and high, or blue, cream and red under Colour-safe view.
    </li>
    <li>The <strong>Total</strong> row at the bottom sums only the rows currently loaded.</li>
</ul>

<h4>The two indicator columns</h4>
<p>
    <strong>Status</strong> carries the record's status icon, with the status and any
    note in the tooltip. Two things there are worth knowing:
</p>
<ul>
    <li>
        A <strong>crossed-out circle means Sold Out</strong> &mdash; this journey's
        intraday bookings have reached or passed its own allocation. It is about the
        journey, not the train: the leg may still have seats for other O&amp;Ds.
    </li>
    <li>
        On an <strong>Override Watch</strong> or <strong>Override Ignore</strong> row the
        icon is clickable and opens <strong>Override Audit</strong> &mdash; every override
        recorded against that journey, with the time, the user, the status, the profile,
        the action and any comment.
    </li>
</ul>
<p>
    <strong>BR</strong> reports what the business rules did to the row:
</p>
<ul>
    <li><strong>Filled green</strong> &mdash; a rule applied.</li>
    <li>
        <strong>Filled red</strong> &mdash; a rule applied, but at least one other rule
        that matched this journey did not take effect. Either it was beaten on priority,
        or it was a Max Alloc rule that won on priority and then had its cap removed by a
        bucket-order conflict. The tooltip says which.
    </li>
    <li><strong>Hollow orange</strong> &mdash; a rule is pending.</li>
</ul>
<p>
    The green and red dots are the same shape, so the red one always carries an exclamation
    mark. That does not wait on Colour-safe view.
</p>
<p>
    Clicking a green or red dot opens the rule detail, which now carries two extra
    sections where they apply: <strong>Applied But No Effect</strong> for a nullified
    Max Alloc cap, and <strong>Not Applied &mdash; Beaten On Priority</strong>, which
    lists each losing rule, its priority and the rule that beat it. The
    <strong>Re-prioritize</strong> buttons underneath open the Rule Priorities screen
    with that journey already filtered in. A priority change only bites on the next
    overnight run.
</p>

<h4>Overriding</h4>
<p>
    <strong>Click the Prev Profile cell</strong> to open the override window for a
    single journey. There you can either type seat counts against individual buckets
    or pick a different profile, which fills the buckets in for you. Protected,
    closed and invalid rows are colour-coded. <strong>Override</strong> saves;
    <strong>Approve</strong> accepts the system recommendation instead.
</p>
<p>
    Either button then asks <strong>whether the change should reach the reservation
    system now</strong>. <strong>Save &amp; Send Now</strong> sends it immediately;
    <strong>Save Only</strong> keeps the old behaviour and leaves it for the next
    scheduled export; Cancel, and the X, abandon the change altogether. If the save
    goes through but the send does not, the window stays open and says so &mdash; the
    override still stands.
</p>
<p>
    <strong>Bulk Overrides</strong> in the toolbar applies to every row you have
    ticked. The same dialog opens from the heatmap and tube map for a whole train or
    leg. Applying one now asks you to confirm, and the confirmation states the scope
    it is about to cover &mdash; O&amp;D pairs, RSIDs and dates, with an upper bound on
    the journey/date combinations that follow from them.
</p>
<ul>
    <li>
        <strong>Bulk bucket overrides are entered as percentages</strong>, while single
        overrides are seat counts. The two dialogs use the same word for both.
    </li>
    <li>
        <strong>Saving an override always changes the record status</strong> to Override
        Watch, or Override Ignore if you set that toggle. You cannot save one without
        the other.
    </li>
    <li>
        <strong>Approve removes any override</strong> on the journey, along with its
        reminder, and returns it to the system values. That is the only way to undo an
        override from this screen.
    </li>
    <li>
        A bulk <em>bucket</em> override launched from a leg or train menu applies to
        <strong>every journey on that train</strong>, not just the leg you clicked. Use
        the grid's Bulk Overrides button if you want it limited to particular journeys.
    </li>
    <li>
        <strong>A bulk Approve discards the bucket figures you typed in that dialog</strong>
        and submits the recommended values instead. The confirmation says so. A variable
        profile change is the exception &mdash; that one is submitted with the approval.
    </li>
</ul>

<h4>Walk-up overrides</h4>
<p>
    <strong>Override Walk-Up</strong> on the heatmap or tube map adjusts walk-up
    demand per leg, by number or percentage, with a Revert to undo. It is blocked
    while a re-optimisation is running.
</p>

<h4>Re-optimising and sending to RARS</h4>
<p>
    <strong>Re-Optimize</strong> now asks what should happen to the results before it
    starts anything. <strong>Send Automatically</strong> queues the send itself as
    soon as every job finishes, with no review step. <strong>Allow Review</strong>
    leaves the results in FareVantage for you to check. Cancel starts no
    re-optimisation at all.
</p>
<p>
    The button beside it is the review path. It reads <strong>Review &amp; Send</strong>
    with a count of affected journeys once a completed re-optimisation, or an
    immediate business-rule apply, is waiting. Otherwise it is disabled with a tooltip
    saying why &mdash; no portfolio selected, nothing waiting, or a send already running.
</p>
<p>
    The review dialog shows the prepared payload as it will be sent, one row per
    journey: departure, service, journey, class, <strong>Source</strong> &mdash; the
    business rule that put the journey there, <em>Reopt</em>, or <em>Bulk Inventory
    Override</em> &mdash; the UIC OD pair RARS actually receives, and the number of
    buckets. The <strong>+</strong> on a row expands it to the bucket rows with their
    current and new limits. A row marked <em>parent of n</em> is an OD group parent
    carrying the limits for its children.
</p>
<p>
    Every journey starts ticked. <strong>Untick a journey to hold its allocations
    back</strong> from this send; the line above the grid says how many are going.
    <strong>Send to RARS</strong> sends the ticked journeys. <strong>Close</strong>
    keeps the prepared payload, and reopening it is instant. <strong>Discard</strong>
    means do not send now: the payload is set aside and the overnight run sends the
    latest numbers instead. Journeys you unticked are recorded as excluded and also go
    with the overnight run.
</p>
<ul>
    <li>
        <strong>Prepared figures go stale.</strong> Past about half an hour the dialog
        warns that bookings may have moved since. Discard it if those numbers should no
        longer go; the overnight run sends fresh ones either way.
    </li>
    <li>
        If sending is switched off for your operator the dialog reports
        <strong>Blocked</strong> and nothing is sent. That is a configuration state, not
        a failure.
    </li>
    <li>
        Progress arrives in the <strong>notification bell</strong> &mdash; being prepared,
        ready to review, sending, completed, partly failed, blocked, cancelled or expired.
        Opening a finished one takes you to Inventory filtered to the affected dates.
    </li>
    <li>
        Only one send runs at a time for the whole operator, so the button can be disabled
        because of someone else's send.
    </li>
</ul>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>The grid figures will not move after you save an override.</strong>
        Allocation, remaining demand and excess come from the last optimiser run;
        your override is stored separately and picked up downstream. Only the profile
        and status columns change straight away.
    </li>
    <li>
        <strong>Do not add Empty Seats up across the journeys on one train.</strong> Most
        of them are measured on the same physical leg, so the Total row's sum counts the
        same seats several times over. It is a per-journey read, not a train total. Flex
        demand is not netted off it yet either, so a class carrying flex reads high.
    </li>
    <li>
        <strong>Sold Out is worked out on screen, not sent by the server.</strong> It is
        not one of the values in the <strong>Record Status</strong> filter, so you cannot
        search for it, and its icon replaces whatever status icon the row would otherwise
        have shown. The tooltip still names the real status, and an override row still
        opens its audit trail.
    </li>
    <li>
        <strong>If someone else changes a journey while your override window is
        open</strong>, saving is refused with a <strong>Save Conflict</strong> message.
        Reload refreshes both the window and the row behind it; you then re-enter the
        change against the current numbers.
    </li>
    <li>
        <strong>A child O&amp;D in an OD group cannot be sent on its own.</strong> Limits
        reach the reservation system under the parent, so the send question is not offered
        at all &mdash; the override saves and goes with the next scheduled export.
    </li>
    <li>
        <strong>Export View does not match what you see.</strong> It uses a fixed column
        list, ignores the columns you have hidden, and a couple of its time columns come
        out blank.
    </li>
    <li>
        Right-clicking the <strong>Excess</strong> or <strong>Comp Price Diff</strong>
        headers hides or reveals those columns.
    </li>
    <li>
        Which columns are available at all is set per operator, so a column missing from
        the <strong>Metrics:</strong> list cannot be switched on here.
    </li>
    <li>
        Depending on your operator's settings, journeys with nothing bookable may be
        hidden from the grid, which also changes the totals.
    </li>
</ul>
